import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  CalendarIcon,
  CheckIcon,
  LockIcon,
  LucideAngularModule,
  LucideIconData,
  PackageIcon,
  PlugZap,
  ShelvingUnit,
  ShoppingCart,
  UsersIcon,
  XIcon,
  ZapIcon,
} from 'lucide-angular';
import { ModalAction } from '../../../enums/modal-action.enum';
import { ModalComponent, ModalResult } from '../../../models/modal/modal-component.model';
import { OrganizationModule } from '../../../enums/organization-module.enum';
import { OrganizationService } from '../../../services/organization/organization.service';
import { AnalyticsService } from '../../../services/analytics/analytics.service';
import {
  MODULE_REQUIRED_PLAN,
  PLAN_BADGE_COLOR,
  PLAN_DISPLAY_NAME,
} from '../../../configs/module-plan.config';
import { MODULE_LABELS, PLAN_FEATURES } from '../../../configs/plan-features.config';
import { SubscriptionPlan } from '../../../enums/subscription-plan.enum';

export interface UpgradeModalData {
  /** Module-gated feature (sidebar, settings tabs). */
  module?: OrganizationModule;
  /** Non-module feature gated to a plan (e.g. custom roles). */
  feature?: GatedFeature;
}

/** A plan-gated feature that is not an OrganizationModule. */
export interface GatedFeature {
  /** Localized label shown in the modal. Matching PLAN_FEATURES labels are
   * filtered out of the "Also included" list to avoid duplication. */
  label: string;
  icon: LucideIconData;
  requiredPlan: SubscriptionPlan;
  /** Stable identifier for analytics, e.g. 'roles'. */
  analyticsKey: string;
}

const MODULE_ICONS: Record<OrganizationModule, LucideIconData> = {
  [OrganizationModule.Orders]: ShoppingCart,
  [OrganizationModule.Products]: PackageIcon,
  [OrganizationModule.Customers]: UsersIcon,
  [OrganizationModule.Inventory]: ShelvingUnit,
  [OrganizationModule.Calendar]: CalendarIcon,
  [OrganizationModule.Organization]: ZapIcon,
  [OrganizationModule.Integrations]: PlugZap,
};

@Component({
  selector: 'skk-upgrade-modal',
  imports: [LucideAngularModule],
  templateUrl: './upgrade-modal.html',
  styleUrl: './upgrade-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpgradeModal implements ModalComponent<UpgradeModal, never, UpgradeModalData> {
  dialogRef: DialogRef<ModalResult<never>, UpgradeModal> = inject(DialogRef);
  readonly dialogData: UpgradeModalData = inject(DIALOG_DATA);

  private readonly router = inject(Router);
  private readonly organizationService = inject(OrganizationService);
  private readonly analytics = inject(AnalyticsService);

  readonly xIcon = XIcon;
  readonly checkIcon = CheckIcon;
  readonly lockIcon = LockIcon;

  readonly module = this.dialogData.module ?? null;
  private readonly feature = this.dialogData.feature ?? null;

  readonly moduleLabel = this.module ? MODULE_LABELS[this.module] : (this.feature?.label ?? '');
  readonly moduleIcon = this.module ? MODULE_ICONS[this.module] : (this.feature?.icon ?? ZapIcon);

  readonly requiredPlan = this.module
    ? MODULE_REQUIRED_PLAN[this.module]
    : (this.feature?.requiredPlan ?? SubscriptionPlan.PRO);
  readonly requiredPlanName = PLAN_DISPLAY_NAME[this.requiredPlan];
  readonly badgeVariant = PLAN_BADGE_COLOR[this.requiredPlan];

  readonly currentPlan = computed(
    () => this.organizationService.organization()?.subscription?.plan ?? SubscriptionPlan.NONE,
  );
  readonly currentPlanName = computed(() => PLAN_DISPLAY_NAME[this.currentPlan()]);

  readonly includedFeatures = PLAN_FEATURES[this.requiredPlan].filter((f) =>
    this.module ? f.module !== this.module : f.label !== this.feature?.label,
  );

  close(): void {
    this.dialogRef.close({ action: ModalAction.Close });
  }

  submit(): void {
    this.dialogRef.close({ action: ModalAction.Submit });
  }

  goToPlan(): void {
    this.analytics.track('Upgrade Prompt Clicked', {
      module: this.module ?? this.feature?.analyticsKey ?? 'unknown',
      requiredPlan: this.requiredPlan,
    });
    this.close();

    // Send the user straight to the plan screen for better conversion (instead
    // of the billing settings page). Mirror the billing "Change plan" routing:
    // an org with a *live* (active/trialing) subscription edits it in-app (plan
    // screen in "change" mode); anything else - no subscription or a dead one
    // (canceled/unpaid) - goes through Checkout, where the backend self-heals
    // stale references and starts a fresh subscription.
    const status = this.organizationService.organization()?.subscription?.status;
    const isStripeManaged = status === 'active' || status === 'trialing';
    if (isStripeManaged) {
      void this.router.navigate(['/onboarding', 'plan'], {
        queryParams: { mode: 'change' },
      });
      return;
    }

    void this.router.navigate(['/onboarding', 'plan']);
  }
}
